/**
 * AdminMasterServicesPage - Iter 9.40+ category × service_type grouping
 *
 * Two-level accordion:
 *   Category (Hair, Makeup, …) ─┬─ Service Type (Cut, Color, …)  ── Services list
 *                                └─ Service Type (…)               ── Services list
 *
 * Mirrors the Master Products (category → brand → products) pattern so admins
 * have a consistent editing surface across master DBs. Each service is
 * inline-editable; admins can add new service_types as they go (free text).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Briefcase, Plus, Save, Trash2, ChevronDown, ChevronRight, Loader2, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });
const titleCase = (s) =>
  (s || "")
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

// Suggested canonical types per category - the admin can still type any value.
const TYPE_SUGGESTIONS = {
  hair:        ["Cut", "Color", "Highlights", "Treatment", "Blowout", "Style", "Extensions", "Bridal"],
  makeup:      ["Application", "Lesson", "Glam", "Editorial", "Bridal", "SFX", "Lashes"],
  nails:       ["Manicure", "Pedicure", "Gel", "Acrylic", "Nail Art"],
  lashes:      ["Classic Set", "Hybrid Set", "Volume Set", "Fill", "Removal", "Strip Lashes"],
  culinary:    ["Tasting", "Private Chef", "Catering", "Cooking Class"],
  style:       ["Personal Styling", "Closet Edit", "Shopping", "Look Build"],
  wellness:    ["Massage", "Facial", "Body Treatment", "Yoga", "Sound Bath"],
  events:      ["Bridal Suite", "Event Glam Team", "On-site"],
  dispensary:  ["Consult", "Sampling", "Curation"],
  instructor:  ["Group Class", "Private Lesson", "Workshop", "Series"],
};

const CATEGORY_OPTIONS = Object.keys(TYPE_SUGGESTIONS);

export default function AdminMasterServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({}); // {`${cat}` or `${cat}::${type}`: true}
  const [creatingIn, setCreatingIn] = useState(null); // {cat, type}
  const [draft, setDraft] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/master-services`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setServices(d.services || d || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group: category → service_type → services[]
  const grouped = useMemo(() => {
    const cats = {};
    for (const s of services) {
      const cat = s.category || "uncategorized";
      const typ = s.service_type || "Other";
      cats[cat] = cats[cat] || { types: {}, total: 0 };
      cats[cat].types[typ] = cats[cat].types[typ] || [];
      cats[cat].types[typ].push(s);
      cats[cat].total += 1;
    }
    return cats;
  }, [services]);

  const toggle = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));

  const startCreate = (cat, type) => {
    setCreatingIn({ cat, type });
    setDraft({
      name: "", category: cat, service_type: type, duration_min: 60, price_usd: 0,
      description: "", tags: [], available_in_instructor: false,
    });
  };
  const cancelCreate = () => { setCreatingIn(null); setDraft({}); };

  const saveCreate = async () => {
    if (!draft.name) { toast.error("Name required"); return; }
    const r = await fetch(`${API}/admin/master-services`, {
      method: "POST", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(draft),
    });
    if (r.ok) { toast.success(`${draft.name} created`); cancelCreate(); load(); }
    else toast.error("Create failed");
  };

  const startEdit = (s) => {
    setEditingId(s.service_id);
    setEditDraft({
      name: s.name, category: s.category, service_type: s.service_type || "Other",
      duration_min: s.duration_min || 60, price_usd: s.price_usd || 0,
      description: s.description || "", available_in_instructor: !!s.available_in_instructor,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const saveEdit = async () => {
    const r = await fetch(`${API}/admin/master-services/${editingId}`, {
      method: "PATCH", headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(editDraft),
    });
    if (r.ok) { toast.success("Saved"); cancelEdit(); load(); }
    else toast.error("Save failed");
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Hosts who linked it will lose the link.`)) return;
    const r = await fetch(`${API}/admin/master-services/${s.service_id}`, { method: "DELETE", headers: auth() });
    if (r.ok) { toast.success("Deleted"); load(); }
    else toast.error("Delete failed");
  };

  const promptNewType = (cat) => {
    const t = window.prompt(`New service type under ${titleCase(cat)}:`);
    if (!t) return;
    startCreate(cat, t.trim());
  };

  const allCategories = useMemo(() => {
    const fromData = Object.keys(grouped);
    return Array.from(new Set([...CATEGORY_OPTIONS, ...fromData])).sort();
  }, [grouped]);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-master-services-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Briefcase className="w-5 h-5" /> Master Services</h1>
          <p className="text-xs text-stone-500">Grouped by category · service type. Hosts link these to their dashboards and can rename per-host.</p>
        </div>
        <span className="text-xs text-stone-400">{services.length} total</span>
      </div>

      {loading && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>}

      {!loading && (
        <div className="space-y-3">
          {allCategories.map((cat) => {
            const data = grouped[cat] || { types: {}, total: 0 };
            const catCollapsed = !!collapsed[cat];
            const catKey = cat;
            const knownTypes = Array.from(new Set([...(TYPE_SUGGESTIONS[cat] || []), ...Object.keys(data.types)]));
            return (
              <Card key={cat} className="border border-stone-200 overflow-hidden" data-testid={`svc-cat-${cat}`}>
                <button
                  type="button"
                  onClick={() => toggle(catKey)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-stone-50"
                  data-testid={`svc-cat-toggle-${cat}`}
                >
                  <div className="flex items-center gap-2">
                    {catCollapsed ? <ChevronRight className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                    <span className="font-semibold text-sm">{titleCase(cat)}</span>
                    <Badge className="bg-stone-100 text-stone-600 border-0 text-[10px]">{data.total}</Badge>
                  </div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); promptNewType(cat); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); promptNewType(cat); } }}
                    className="text-[11px] text-purple-600 hover:text-purple-800 inline-flex items-center gap-1 cursor-pointer"
                    data-testid={`svc-cat-add-type-${cat}`}
                  >
                    <Plus className="w-3 h-3" /> Add service type
                  </span>
                </button>

                {!catCollapsed && (
                  <CardContent className="pt-0 space-y-2">
                    {knownTypes.length === 0 && (
                      <p className="text-xs text-stone-400 text-center py-4">No service types yet - click "+ Add service type".</p>
                    )}
                    {knownTypes.map((typ) => {
                      const rows = (data.types[typ] || []);
                      const typeKey = `${cat}::${typ}`;
                      const typeCollapsed = !!collapsed[typeKey];
                      const isCreating = creatingIn?.cat === cat && creatingIn?.type === typ;
                      return (
                        <div key={typ} className="border border-stone-100 rounded-md" data-testid={`svc-type-${cat}-${typ}`}>
                          <button
                            type="button"
                            onClick={() => toggle(typeKey)}
                            className="w-full px-3 py-2 flex items-center gap-2 hover:bg-stone-50 rounded-t-md"
                            data-testid={`svc-type-toggle-${cat}-${typ}`}
                          >
                            {typeCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
                            <span className="text-xs font-semibold text-stone-700">{typ}</span>
                            <Badge className="bg-stone-100 text-stone-600 border-0 text-[10px] ml-1">{rows.length}</Badge>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); startCreate(cat, typ); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); startCreate(cat, typ); } }}
                              className="ml-auto text-[10px] text-purple-600 hover:text-purple-800 inline-flex items-center gap-1 cursor-pointer"
                              data-testid={`svc-type-add-${cat}-${typ}`}
                            >
                              <Plus className="w-3 h-3" /> Add service
                            </span>
                          </button>
                          {!typeCollapsed && (
                            <div className="p-2 pt-0 space-y-1">
                              {isCreating && (
                                <div className="border border-amber-200 bg-amber-50 rounded p-3" data-testid={`svc-create-form-${cat}-${typ}`}>
                                  <p className="text-xs font-semibold mb-2">New service in {titleCase(cat)} · {typ}</p>
                                  <ServiceForm draft={draft} setDraft={setDraft} testidPrefix={`svc-new-${cat}-${typ}`} />
                                  <div className="flex gap-2 mt-2">
                                    <Button size="sm" onClick={saveCreate} className="bg-purple-600 text-white" data-testid={`svc-create-save-${cat}-${typ}`}>
                                      <Save className="w-3.5 h-3.5 mr-1" /> Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelCreate}>Cancel</Button>
                                  </div>
                                </div>
                              )}
                              {rows.length === 0 && !isCreating && (
                                <p className="text-[11px] text-stone-400 text-center py-3">No services in this type yet.</p>
                              )}
                              {rows.map((s) =>
                                editingId === s.service_id ? (
                                  <div key={s.service_id} className="border rounded p-3 bg-stone-50" data-testid={`svc-edit-form-${s.service_id}`}>
                                    <ServiceForm draft={editDraft} setDraft={setEditDraft} testidPrefix={`svc-edit-${s.service_id}`} />
                                    <div className="flex gap-2 mt-2">
                                      <Button size="sm" onClick={saveEdit} className="bg-purple-600 text-white" data-testid={`svc-edit-save-${s.service_id}`}>
                                        <Save className="w-3.5 h-3.5 mr-1" /> Save
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    key={s.service_id}
                                    className="flex items-center gap-2 p-2 hover:bg-stone-50 rounded group"
                                    data-testid={`svc-row-${s.service_id}`}
                                  >
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-medium text-stone-900 truncate">{s.name}</p>
                                      <p className="text-[10px] text-stone-400">{s.duration_min}m · ${s.price_usd ?? 0}{s.available_in_instructor ? " · instructor" : ""}</p>
                                    </div>
                                    {(s.tags || []).slice(0, 2).map((t) => (
                                      <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                                    ))}
                                    <Button size="sm" variant="ghost" className="h-7 px-2 opacity-0 group-hover:opacity-100" onClick={() => startEdit(s)} data-testid={`svc-edit-${s.service_id}`}>
                                      <Edit3 className="w-3 h-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 px-2 text-stone-400 hover:text-red-500 opacity-0 group-hover:opacity-100" onClick={() => remove(s)} data-testid={`svc-del-${s.service_id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ServiceForm({ draft, setDraft, testidPrefix }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Name</label>
        <Input className="h-8 text-xs" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid={`${testidPrefix}-name`} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Service type</label>
        <Input className="h-8 text-xs" value={draft.service_type || ""} onChange={(e) => setDraft({ ...draft, service_type: e.target.value })} placeholder="Cut, Color, Highlights…" data-testid={`${testidPrefix}-type`} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Category</label>
        <Select value={draft.category || ""} onValueChange={(v) => setDraft({ ...draft, category: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${testidPrefix}-category`}><SelectValue placeholder="Pick category" /></SelectTrigger>
          <SelectContent>
            {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{titleCase(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Duration (min)</label>
        <Input type="number" className="h-8 text-xs" value={draft.duration_min ?? 60} onChange={(e) => setDraft({ ...draft, duration_min: parseInt(e.target.value) || 0 })} data-testid={`${testidPrefix}-duration`} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Price (USD)</label>
        <Input type="number" className="h-8 text-xs" value={draft.price_usd ?? 0} onChange={(e) => setDraft({ ...draft, price_usd: parseFloat(e.target.value) || 0 })} data-testid={`${testidPrefix}-price`} />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-stone-500 block mb-0.5">Description</label>
        <Input className="h-8 text-xs" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid={`${testidPrefix}-desc`} />
      </div>
      <label className="md:col-span-3 flex items-center gap-2 text-[11px] text-stone-600">
        <input type="checkbox" checked={!!draft.available_in_instructor} onChange={(e) => setDraft({ ...draft, available_in_instructor: e.target.checked })} data-testid={`${testidPrefix}-instructor`} />
        Also available in Instructor add-on dashboards
      </label>
    </div>
  );
}
